// Accessible display mode: 'normal' or 'simple' (large text + big buttons), for
// users who find the standard UI hard to use. Mirrors the language preference:
// persisted in localStorage and applied as a data-attribute on <html>, with a
// signed-in user's saved choice taking over (see AppLayout).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useI18n } from './i18n'
import type { DisplayMode } from './types'

const MODES: DisplayMode[] = ['normal', 'simple']
const STORAGE_KEY = 'display-mode'

interface DisplayModeValue {
  mode: DisplayMode
  setMode: (m: DisplayMode) => void
}

const DisplayModeContext = createContext<DisplayModeValue | null>(null)

function readMode(): DisplayMode {
  const stored = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) as DisplayMode | null
  return stored && MODES.includes(stored) ? stored : 'normal'
}

export function DisplayModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DisplayMode>(() => readMode())

  // Apply to the root element so a single CSS block can restyle everything.
  useEffect(() => {
    document.documentElement.dataset.mode = mode
  }, [mode])

  const setMode = useCallback((m: DisplayMode) => {
    setModeState(m)
    try {
      localStorage.setItem(STORAGE_KEY, m)
    } catch {
      /* ignore */
    }
  }, [])

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode])
  return <DisplayModeContext.Provider value={value}>{children}</DisplayModeContext.Provider>
}

export function useDisplayMode(): DisplayModeValue {
  const ctx = useContext(DisplayModeContext)
  if (!ctx) throw new Error('useDisplayMode must be used within DisplayModeProvider')
  return ctx
}

// Big segmented control to switch between normal and large/simple UI.
export function DisplayModeToggle() {
  const { mode, setMode } = useDisplayMode()
  const { t } = useI18n()
  return (
    <div className="segmented mode-toggle">
      <button type="button" className={mode === 'normal' ? 'active' : ''} onClick={() => setMode('normal')}>
        {t('mode.normal')}
      </button>
      <button type="button" className={mode === 'simple' ? 'active' : ''} onClick={() => setMode('simple')}>
        {t('mode.large')}
      </button>
    </div>
  )
}
